import { UserProfile } from 'fitbeat.gym-core';
import { BaseEquipment, GymTypes } from 'fitbeat.models';
import React from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { connect } from 'react-redux';
import { Button, ButtonTypes } from '../components';
import { GymResolver } from '../services/GymResolver';
import theme from '../theme';
import { defaultScale } from '../utils/scaling';
import { dumbbellFlowSelected, manualFlowSelected, uniBandsFlowSelected } from './actions';
import {
  IStrengthResult,
  selectIsAssessmentResultLoading,
  selectStrengthAssessmentResultsBeforeAssessment,
} from './selectors';
import { StrengthSummary } from './StrengthSummary';

interface IStateProps {
  results: IStrengthResult[];
  isAssessmentResultLoading: boolean;
}

interface IDispatchProps {
  manualAssessmentSelected(): Promise<void>;
  uniBandsAssessmentSelected(): Promise<void>;
  dumbbellAssessmentSelected(): Promise<void>;
}
interface IProps {
  userProfile: UserProfile;
  getNextStep(): void;
}

type ComponentProps = IProps & IStateProps & IDispatchProps;

export const StrengthSettingsComponent: React.FC<ComponentProps> = ({
  results,
  manualAssessmentSelected,
  uniBandsAssessmentSelected,
  dumbbellAssessmentSelected,
  isAssessmentResultLoading,
  userProfile,
}) => {
  const showDumbbellAssessment = userProfile.gymName
    && (
      BaseEquipment.isHomeDumbbellsEquipmentSelection(userProfile.defaultEquipment)
      || GymResolver.getByName(userProfile.gymName).type === GymTypes.Indoor
    );

  const showBandsAssessment = userProfile.gymName &&
    GymResolver.getByName(userProfile.gymName).type === GymTypes.Home && !showDumbbellAssessment;

  if (isAssessmentResultLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size='small' color={theme.PRIMARY} animating={true} />
      </View>
    );
  }
  return (
    <View style={styles.container}>
      <StrengthSummary results={results} gender={userProfile.gender} />
      <Button
        title={'Recalibrate manually'}
        fontColor={theme.WHITE}
        onPress={manualAssessmentSelected}
        containerStyle={styles.buttonContainer}
      />
      {showBandsAssessment && (
        <Button
          title={'Recalibrate with Universal Bands'}
          fontColor={theme.PRIMARY}
          onPress={uniBandsAssessmentSelected}
          containerStyle={styles.buttonContainer}
          buttonType={ButtonTypes.TRANSPARENT}
        />
      )}
      {showDumbbellAssessment && (
        <Button
          title={'Recalibrate with Dumbbells'}
          fontColor={theme.PRIMARY}
          onPress={dumbbellAssessmentSelected}
          containerStyle={styles.buttonContainer}
          buttonType={ButtonTypes.TRANSPARENT}
        />
      )}
    </View>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    results: selectStrengthAssessmentResultsBeforeAssessment(
      state,
      ownProps.userProfile,
    ),
    isAssessmentResultLoading: selectIsAssessmentResultLoading(state),
  };
};

const mapDispatchToProps = (dispatch, ownProps: IProps) => {
  const { getNextStep } = ownProps;
  return {
    manualAssessmentSelected: async () => {
      await dispatch(manualFlowSelected());
      getNextStep();
    },
    uniBandsAssessmentSelected: async () => {
      await dispatch(uniBandsFlowSelected());
      getNextStep();
    },
    dumbbellAssessmentSelected: async () => {
      await dispatch(dumbbellFlowSelected());
      getNextStep();
    },
  };
};

export const StrengthSettings: React.FC<IProps> = connect<
  IStateProps,
  IDispatchProps,
  IProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(StrengthSettingsComponent);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  buttonContainer: {
    marginHorizontal: defaultScale(15),
    height: defaultScale(45),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
